import { useTranslations } from 'next-intl';
import type { Extend } from '@mentimeter/ragnar-dsc';
import { PencilIcon } from '@mentimeter/ragnar-visuals';
import React, { useEffect, useRef, useState } from 'react';
import { Box } from '@mentimeter/ragnar-ui/box';
import { Clickable } from '@mentimeter/ragnar-ui/clickable';
import { Text } from '@mentimeter/ragnar-ui/text';
import { TextInput } from '@mentimeter/ragnar-ui/input/text-input';

const fullOpacityOnHover: Extend = () => ({
  '@media (hover: hover)': {
    ':hover': {
      opacity: 1,
    },
  },
});

interface EditableTitleProps {
  titleText: string;
  onTextUpdate: (text: string) => void;
  placeholder: string;
  isEditable: boolean;
}

export const EditableTitle = ({
  titleText,
  onTextUpdate,
  placeholder,
  isEditable,
}: EditableTitleProps) => {
  const t = useTranslations('presenting');
  const NAME_MAX_CHAR = 50;
  const [isEditing, setIsEditing] = useState(false);
  const [text, setText] = useState(titleText);
  const [isHovering, setIsHovering] = useState<boolean>(false);
  const container = useRef<HTMLDivElement | null>(null);
  const [inputWidth, setInputWidth] = useState(container?.current?.offsetWidth);

  const handleBlur = (e: React.ChangeEvent<HTMLInputElement>) => {
    onTextUpdate(e.currentTarget.value);
    setIsEditing(false);
  };

  const handleClick = () => {
    if (!isEditable) return;
    setIsEditing(true);
  };

  const updateInputWidth = () => {
    if (container.current && container.current.offsetWidth > 0) {
      setInputWidth(container.current.offsetWidth + 100);
    }
  };

  useEffect(() => {
    updateInputWidth();
  }, [inputWidth]);

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setText(e.target.value);
    updateInputWidth();
  };

  useEffect(() => {
    setText(titleText);
  }, [titleText]);

  return (
    <Box
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      onClick={() => setIsHovering(false)}
    >
      <Box
        flexDirection="row"
        width="100%"
        extend={() => ({
          visibility: isEditing ? 'hidden' : 'visible',
        })}
      >
        <Clickable
          onClick={handleClick}
          style={isEditable ? { cursor: 'pointer' } : { cursor: 'default' }}
          ref={container}
          extend={fullOpacityOnHover}
        >
          <Text
            fontSize="40px"
            fontWeight="regular"
            fontFamily="heading"
            lineHeight="heading"
            color="text"
          >
            {text ? text : placeholder}
          </Text>
        </Clickable>
        {isHovering && isEditable && (
          <Box ml={2} onClick={handleClick} style={{ cursor: 'pointer' }}>
            <PencilIcon color="textDisabled" size={7} />
          </Box>
        )}
      </Box>
      {isEditing && (
        <Box width={inputWidth} mt="-70px" ml="-7px" minWidth="350px">
          <TextInput
            width="100%"
            aria-label={t('grouping_responses.editable_title_group_title_a11y')}
            type="text"
            placeholder={placeholder}
            value={text}
            onChange={handleTextChange}
            key={titleText}
            name={titleText}
            autoFocus
            onBlur={handleBlur}
            maxLength={NAME_MAX_CHAR}
            style={{
              fontSize: '40px',
              fontWeight: 'regular',
              lineHeight: 'heading',
            }}
          />
        </Box>
      )}
    </Box>
  );
};
